import React from 'react'
import PropTypes from 'prop-types'
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'packages/eid-ui'
import * as DynamicComponents from 'components/DynamicComponents'
import { isNilOrEmpty } from 'packages/core'
import { TextFormatter, styles, colors } from './utils'
import config from 'config'

const StyledTable = styled(Table)(({ theme }) => styles.table)

const StyledTableRow = styled(TableRow)(({ theme }) => styles.tableRow)

const StyledTableCell = styled(TableCell)(({ theme }) => styles.tableCell)

const HeaderContent = styled('span')(() => styles.tableHeaderContent)

const IconContainer = styled('span')(() => ({
    color: colors.primary,
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '4px',
}))

const IconImage = styled('img')(() => styles.iconRegular)

const PeopleTable = ({
    route,
    headings,
    data,
    sort,
    sortBy,
    sortOrder,
    fallbackMessage,
}) => {
    const { t } = useTranslation()

    const getHeadingName = (heading) =>
        heading.columnName ? heading.columnName : heading.name
    const handleSort = (heading) => {
        sort(
            getHeadingName(heading),
            sortBy !== getHeadingName(heading)
                ? 'desc'
                : !sortOrder || sortOrder === '' || sortOrder === 'asc'
                ? 'desc'
                : 'asc',
        )
    }

    const renderComponent = (a, item) => {
        const DynamicComponentToRender = DynamicComponents[a.component.name]

        return <DynamicComponentToRender attribute={a} data={item} />
    }

    const renderTableContentCell = (a, row) => {
        const cellProps = {
            align: a.align,
            style: a.styles,
        }

        return (
            <React.Fragment key={a.name}>
                {!a.hideValue &&
                    (isNilOrEmpty(row[a.name]) ? (
                        <StyledTableCell {...cellProps} style={null}>
                            -
                        </StyledTableCell>
                    ) : (
                        <StyledTableCell {...cellProps}>
                            {a.name === 'email' ? (
                                <DynamicComponents.CellEmailLink
                                    attribute={a}
                                    data={row}
                                    styles={cellProps.style}
                                    value={
                                        <TextFormatter
                                            item={row}
                                            attribute={a}
                                        />
                                    }
                                />
                            ) : a.link ? (
                                <DynamicComponents.CellLink
                                    attribute={a}
                                    data={row}
                                    styles={cellProps.style}
                                    value={
                                        <TextFormatter
                                            item={row}
                                            attribute={a}
                                        />
                                    }
                                />
                            ) : (
                                <TextFormatter item={row} attribute={a} />
                            )}
                        </StyledTableCell>
                    ))}

                {a.avatar && (
                    <StyledTableCell {...cellProps}>
                        <DynamicComponents.CellAvatar
                            data={row}
                            attribute={a}
                        />
                    </StyledTableCell>
                )}
                {a.component && (
                    <StyledTableCell {...cellProps}>
                        {renderComponent(a, row, route)}
                    </StyledTableCell>
                )}
            </React.Fragment>
        )
    }

    const renderIconLocal = (heading) => {
        return (
            heading.icons && (
                <IconContainer>
                    {heading.icons.map((i, index) => (
                        <IconImage
                            key={`icon-image-${heading.name}-${index}`}
                            src={`${config.APP_SUBPATH}${i}`}
                            alt=""
                        />
                    ))}
                </IconContainer>
            )
        )
    }

    return (
        <StyledTable>
            <TableHead variant="eidHead">
                <StyledTableRow>
                    {headings.map((heading) => (
                        <TableCell
                            data-protectedsubcomponent={
                                heading?.requireAccess?.control
                                    ? heading.requireAccess.control
                                    : 'NotProtected'
                            }
                            data-ai-explain={`Column: ${heading.label || heading.name}`}
                            key={heading.name}
                            align={heading.align}
                            scope="col"
                            sx={{
                                paddingBlock: '1.7rem',
                            }}
                        >
                            <>
                                {heading.sort ? (
                                    <Tooltip
                                        title={
                                            sortBy ===
                                                getHeadingName(heading) &&
                                            sortOrder === 'desc'
                                                ? t('SortAscending')
                                                : t('SortDescending')
                                        }
                                    >
                                        <TableSortLabel
                                            active={
                                                sortBy ===
                                                getHeadingName(heading)
                                            }
                                            direction={
                                                sortBy ===
                                                getHeadingName(heading)
                                                    ? sortOrder
                                                    : 'asc'
                                            }
                                            onClick={() => handleSort(heading)}
                                        >
                                            {t(heading.label)}
                                        </TableSortLabel>
                                    </Tooltip>
                                ) : (
                                    <HeaderContent>
                                        {t(heading.label)}
                                        {renderIconLocal(heading)}
                                    </HeaderContent>
                                )}
                            </>
                        </TableCell>
                    ))}
                </StyledTableRow>
            </TableHead>

            <TableBody>
                {data.length === 0 && (
                    <StyledTableRow data-ai-explain="Empty table: no records">
                        <StyledTableCell
                            colSpan={headings.length}
                            style={{ textAlign: 'center' }}
                        >
                            {fallbackMessage}
                        </StyledTableCell>
                    </StyledTableRow>
                )}

                {data.map((row) => (
                    <StyledTableRow key={row.id}>
                        {headings.map((a) => renderTableContentCell(a, row))}
                    </StyledTableRow>
                ))}
            </TableBody>
        </StyledTable>
    )
}

PeopleTable.propTypes = {
    headings: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
}

export default PeopleTable
